import { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config/env';
import { DEBUG } from '../config/debug';

const apiCache = new Map();

/** Máximo de requests simultáneos para no provocar ERR_INSUFFICIENT_RESOURCES. */
const PREFETCH_CONCURRENCY = 8;

export async function prefetchApi(endpoints, options = {}) {
  const list = Array.isArray(endpoints) ? endpoints : [endpoints];
  const { onProgress, ...fetchOptions } = options;

  const finalOptions = {
    credentials: 'include',
    ...fetchOptions
  };

  const total = list.length;
  let completed = 0;

  const notify = (endpoint, fromCache = false) => {
    completed += 1;
    if (typeof onProgress === 'function') {
      onProgress({ completed, total, endpoint, fromCache });
    }
  };

  const fetchOne = async (endpoint) => {
    if (!endpoint) return;

    if (apiCache.has(endpoint)) {
      notify(endpoint, true);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}${endpoint}`, finalOptions);
      if (!res.ok) {
        if (DEBUG) console.warn('⚠️ prefetchApi non-OK', endpoint, res.status);
        notify(endpoint, false);
        return;
      }
      const json = await res.json();
      apiCache.set(endpoint, json);
      notify(endpoint, false);
    } catch (e) {
      if (DEBUG) console.error('🔥 prefetchApi error', endpoint, e.message || e);
      notify(endpoint, false);
    }
  };

  for (let i = 0; i < list.length; i += PREFETCH_CONCURRENCY) {
    const chunk = list.slice(i, i + PREFETCH_CONCURRENCY);
    await Promise.all(chunk.map(fetchOne));
  }
}

export function useApi(endpoint, options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guardamos options en un ref para no reejecutar el efecto en cada render
  // (un objeto literal nuevo por render reiniciaría y cancelaría el fetch en curso).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    let isMounted = true;

    if (!endpoint) {
      setData(null);
      setLoading(false);
      setError(null);
      return () => {
        isMounted = false;
      };
    }

    const cached = apiCache.get(endpoint);
    if (cached) {
      if (isMounted) {
        setData(cached);
        setLoading(false);
        setError(null);
      }
      return () => {
        isMounted = false;
      };
    }

    setData(null);
    setLoading(true);
    setError(null);

    const finalOptions = {
      credentials: 'include',
      ...optionsRef.current
    };

    fetch(`${API_BASE_URL}${endpoint}`, finalOptions)
      .then(res => {
        if (!res.ok) {
          throw new Error(`Error ${res.status}`);
        }

        return res.json();
      })
      .then(json => {
        if (isMounted) {
          setData(json);
          apiCache.set(endpoint, json);
        }
      })
      .catch(err => {
        if (DEBUG) {
          console.error('🔥 Error en fetch:', err);
          console.error('🔥 Mensaje:', err.message);
        }

        if (isMounted) setError(err.message);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [endpoint]);

  return { data, loading, error };
}
